import {
  createPanelFrame,
  resolveTitle,
  createMetric,
  formatDuration,
  formatNumber,
  toNumber,
  clamp
} from './panel-utils.js';
import { createSvg, clearSvg, createSvgEl } from './chart-utils.js';

const DEFAULT_WAVE = Array.from({ length: 120 }, (_, i) => Math.sin(i * 0.2) * 0.6 + Math.sin(i * 0.07) * 0.3);
const DEFAULT_SPECTRO = Array.from({ length: 24 }, (_, r) =>
  Array.from({ length: 40 }, (_, c) => (Math.sin(r * 0.4) + Math.cos(c * 0.2)) * 0.5 + 0.5)
);

export function render({ node, api }) {
  const { root, body, titleEl, metaEl } = createPanelFrame(node, 'Audio Forensics');
  const metrics = document.createElement('div');
  metrics.className = 'ew-metric-grid';

  const waveformWrap = document.createElement('div');
  waveformWrap.className = 'ew-waveform';
  const waveSvg = createSvg(320, 70);
  waveformWrap.appendChild(waveSvg);

  const spectroWrap = document.createElement('div');
  spectroWrap.className = 'ew-spectrogram';
  const specSvg = createSvg(320, 90);
  spectroWrap.appendChild(specSvg);

  const segmentsList = document.createElement('div');
  segmentsList.className = 'ew-list';

  body.append(metrics, waveformWrap, spectroWrap, segmentsList);

  const update = (next) => {
    titleEl.textContent = resolveTitle(next, 'Audio Forensics');
    const duration = toNumber(next?.data?.duration ?? next?.props?.duration, 86.4);
    const sampleRate = toNumber(next?.data?.sampleRate ?? next?.props?.sampleRate, 48000);
    const segments = Array.isArray(next?.data?.segments || next?.props?.segments)
      ? (next.data?.segments || next.props?.segments)
      : [
          { start: 4, end: 18, label: 'Speech' },
          { start: 22, end: 34, label: 'Noise' },
          { start: 36, end: 48, label: 'Keyword: ignite' }
        ];

    metaEl.textContent = `${formatDuration(duration)} runtime`;

    metrics.innerHTML = '';
    metrics.append(
      createMetric('duration', formatDuration(duration)),
      createMetric('sample rate', `${formatNumber(sampleRate / 1000, 0)}khz`),
      createMetric('segments', `${segments.length}`)
    );

    const waveform = Array.isArray(next?.data?.waveform || next?.props?.waveform)
      ? (next.data?.waveform || next.props?.waveform)
      : DEFAULT_WAVE;
    const spectrogram = Array.isArray(next?.data?.spectrogram || next?.props?.spectrogram)
      ? (next.data?.spectrogram || next.props?.spectrogram)
      : DEFAULT_SPECTRO;

    renderWaveform(waveSvg, waveform);
    renderSpectrogram(specSvg, spectrogram);

    segmentsList.innerHTML = '';
    const selected = next?.data?.selectedSegment;
    segments.slice(0, 6).forEach((seg) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'ew-list-item';
      if (selected && selected === seg.label) item.classList.add('is-active');
      const head = document.createElement('div');
      head.className = 'ew-list-head';
      const title = document.createElement('span');
      title.className = 'ew-list-title';
      title.textContent = seg.label || 'Segment';
      const time = document.createElement('span');
      time.className = 'ew-tag';
      time.textContent = `${formatDuration(seg.start)} - ${formatDuration(seg.end)}`;
      head.append(title, time);
      const meta = document.createElement('div');
      meta.className = 'ew-list-meta';
      const dur = formatDuration(Math.max(0, toNumber(seg.end, 0) - toNumber(seg.start, 0)));
      meta.textContent = `duration ${dur}`;
      item.append(head, meta);
      item.addEventListener('click', () => {
        api.setData({ selectedSegment: seg.label });
        api.emit('node:input', { id: node.id, field: 'segment', value: seg.label });
      });
      segmentsList.appendChild(item);
    });
  };

  update(node);
  return { el: root, update };
}

function renderWaveform(svg, values) {
  clearSvg(svg);
  if (!Array.isArray(values) || values.length === 0) return;
  const width = 320;
  const height = 70;
  const mid = height / 2;
  const pad = 6;
  const count = Math.min(160, values.length);
  let d = '';
  for (let i = 0; i < count; i += 1) {
    const idx = Math.floor((i / (count - 1)) * (values.length - 1));
    const val = clamp(toNumber(values[idx], 0), -1, 1);
    const x = pad + (i / (count - 1)) * (width - pad * 2);
    const y = mid - val * (mid - pad);
    d += `${i === 0 ? 'M' : 'L'} ${x} ${y} `;
  }
  const path = createSvgEl('path', {
    d: d.trim(),
    fill: 'none',
    stroke: 'var(--ew-ui-accent)',
    'stroke-width': 2
  });
  svg.appendChild(path);
  const axis = createSvgEl('line', {
    x1: pad,
    y1: mid,
    x2: width - pad,
    y2: mid,
    stroke: 'var(--ew-ui-panel-border)',
    'stroke-opacity': 0.4
  });
  svg.appendChild(axis);
}

function renderSpectrogram(svg, matrix) {
  clearSvg(svg);
  if (!Array.isArray(matrix) || matrix.length === 0) return;
  const rows = matrix.length;
  const cols = matrix[0].length || 0;
  if (!cols) return;
  const width = 320;
  const height = 90;
  const pad = 6;
  const cellW = (width - pad * 2) / cols;
  const cellH = (height - pad * 2) / rows;
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      const val = clamp(toNumber(matrix[r][c], 0), 0, 1);
      const rect = createSvgEl('rect', {
        x: pad + c * cellW,
        y: pad + r * cellH,
        width: cellW,
        height: cellH,
        fill: 'var(--ew-ui-accent)',
        'fill-opacity': 0.1 + val * 0.8
      });
      rect.classList.add('ew-chart-cell');
      svg.appendChild(rect);
    }
  }
}
